// ── Session report STATUS section (status-only, report-EXCLUDED content) ────
// Wave 6 (Sessions). Reports are owned by Wave 7; here we show the session's
// report STATUS ONLY — never report content, summary, highlights, action items,
// or HTML (the no-content rule). Report generation is GATED-DARK: until the gate
// opens the report stays `generating` (generationGate:'closed'), which we render
// honestly. The full report viewer is the Wave-7 route; this panel is a status
// breadcrumb that links there. No score/insight is fabricated.
//
// Note: participant is already DENIED the entire Sessions module (page guard), so
// this panel never renders for a participant — but it still shows STATUS ONLY for
// every role that reaches it, consistent with report-exclusion.

import { Alert, Badge, Button, Module, ModuleBody, ModuleHead, humanizeLabel } from "@vjal/ui";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingArtifactDto } from "../../../services/coaching-dtos";

const services = getCoachingServices();

export function SessionReportStatusSection({ sessionId }: { sessionId: string }) {
  const query = useHonestQuery<CoachingArtifactDto>(
    [...coachingQueryKeys.sessionDetail(sessionId), "report-status"],
    () => services.reports.sessionReport(sessionId)
  );
  const report = query.data;

  return (
    <Module span={6}>
      <ModuleHead title="Report" subtitle="Report status only — the full report opens in the Reports area." />
      <ModuleBody className="space-y-3">
        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No report yet" : undefined}
            message={
              query.posture === "empty"
                ? "A report will be generated after the session is held and ingested."
                : undefined
            }
          />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Badge variant={report?.status === "ready" ? "success" : "warning"}>
                {humanizeLabel(report?.status ?? "generating")}
              </Badge>
            </div>
            <Alert variant="info">
              Report generation runs after the session. Open the Reports area to review the report when it is ready —
              the content is not shown on this status panel.
            </Alert>
            {report?.id ? (
              <Link to={`/reports/${encodeURIComponent(report.id)}`} className="no-underline">
                <Button variant="secondary" size="sm">
                  Open in Reports
                </Button>
              </Link>
            ) : null}
          </>
        )}
      </ModuleBody>
    </Module>
  );
}
